import { autoserialize, autoserializeAs } from 'cerialize';

export enum DonationPaymentType {
  regular = 'regular',
  offline = 'offline',
  manual_payment = 'manual_payment',
  cancellation = 'cancellation',
  floatPayment = 'float_payment',
  terminal = 'terminal',
  // RP-3764 standard-donation contribution kinds: charity requires the contribution
  // (a required pickup fee) vs an optional contribution the donor chose to give.
  charityPickupFee = 'charity_pickup_fee',
  charityContribution = 'charity_contribution',
}

export class DonationPayment implements IDonationPayment {
  @autoserialize completed: boolean;
  @autoserializeAs('auth_completed') authCompleted: boolean;
  @autoserializeAs('setup_completed') setupCompleted: boolean;
  @autoserialize failed: boolean;
  @autoserialize processing: boolean;

  @autoserializeAs('stripe_customer') stripeCustomer?: string;

  @autoserializeAs('stripe_connected_account') stripeConnectedAccount?: string;
  @autoserialize stripe_connected_customer?: string;
  @autoserialize stripe_connected_session?: string;

  @autoserialize stripe_platform_payment_intent?: string;
  @autoserialize stripe_platform_refund?: string;
  @autoserialize stripe_fee?: string;

  @autoserializeAs('stripe_connected_payment_intent')
  stripeConnectedPaymentIntent?: string;
  @autoserializeAs('stripe_connected_refund') stripeConnectedRefund?: string;

  @autoserializeAs('stripe_application_fee') stripeApplicationFee: number;
  @autoserializeAs('stripe_application_fee_refund')
  stripeApplicationFeeRefund?: string;

  @autoserializeAs('payment_type') paymentType?: DonationPaymentType;
  @autoserialize quote_digest?: string;

  @autoserializeAs('booking_intent') bookingIntent?: string; //can refund booking
  @autoserializeAs('booking_refund') bookingRefund?: string; //booking refunded

  @autoserialize stripe_charge?: string;
  @autoserialize stripe_charge_status?: string;

  @autoserializeAs('stripe_payment_intent') stripePaymentIntent?: string; //can refund cancellation
  @autoserializeAs('stripe_refund') stripeRefund?: string; //cancellation refunded

  @autoserializeAs('stripe_connected_invoice') stripeConnectedInvoice?: string;

  @autoserializeAs('stripe_invoice') stripeInvoice?: string;
  @autoserialize stripe_transfer_id?: string;
  @autoserialize stripe_transfer_amount?: number;

  @autoserialize sponsorship_fee_transfer_id?: string;
  @autoserialize sponsorship_transfer_id?: string;
  @autoserialize sponsorship_partner_transfer_id?: string;

  @autoserialize sponsorship_cancellation_transfer_id?: string;

  @autoserialize courier_refund?: string;

  @autoserializeAs('contribution_intent') contributionIntent?: string; //standard-donation contribution charge
  @autoserializeAs('contribution_refund') contributionRefund?: string; //contribution refunded

  constructor(payload?: IDonationPayment) {
    this.completed = !!payload?.completed;
    this.paymentType = payload?.paymentType;
    this.failed = !!payload?.failed;
    this.processing = !!payload?.processing;
    this.authCompleted = !!payload?.authCompleted;
    this.setupCompleted = !!payload?.setupCompleted;
    this.stripeConnectedRefund = payload?.stripeConnectedRefund;
    this.stripeApplicationFeeRefund = payload?.stripeApplicationFeeRefund;
    this.stripeApplicationFee = payload?.stripeApplicationFee || 0;
    this.stripePaymentIntent = payload?.stripePaymentIntent;
    this.stripeRefund = payload?.stripeRefund;
    this.bookingIntent = payload?.bookingIntent;
    this.bookingRefund = payload?.bookingRefund;
    this.stripeConnectedAccount = payload?.stripeConnectedAccount;
    this.stripeConnectedInvoice = payload?.stripeConnectedInvoice;
    this.stripeInvoice = payload?.stripeInvoice;
    this.stripeConnectedPaymentIntent = payload?.stripeConnectedPaymentIntent;
    this.stripeCustomer = payload?.stripeCustomer;
    this.stripe_connected_customer = payload?.stripe_connected_customer;
    this.stripe_connected_session = payload?.stripe_connected_session;
    this.stripe_platform_payment_intent = payload?.stripe_platform_payment_intent;
    this.stripe_platform_refund = payload?.stripe_platform_refund;
    this.stripe_fee = payload?.stripe_fee;
    this.quote_digest = payload?.quote_digest;
    this.stripe_charge = payload?.stripe_charge;
    this.stripe_charge_status = payload?.stripe_charge_status;
    this.stripe_transfer_id = payload?.stripe_transfer_id;
    this.stripe_transfer_amount = payload?.stripe_transfer_amount;
    this.sponsorship_fee_transfer_id = payload?.sponsorship_fee_transfer_id;
    this.sponsorship_transfer_id = payload?.sponsorship_transfer_id;
    this.sponsorship_partner_transfer_id = payload?.sponsorship_partner_transfer_id;
    this.sponsorship_cancellation_transfer_id = payload?.sponsorship_cancellation_transfer_id;
    this.courier_refund = payload?.courier_refund;
    this.contributionIntent = payload?.contributionIntent;
    this.contributionRefund = payload?.contributionRefund;
  }
}

export interface IDonationPayment {
  completed: boolean;
  paymentType?: DonationPaymentType;
  failed: boolean;
  processing: boolean;
  authCompleted: boolean;
  setupCompleted: boolean;
  stripeConnectedRefund?: string;
  stripeApplicationFeeRefund?: string;
  stripeApplicationFee?: number;
  stripePaymentIntent?: string;
  stripeRefund?: string;
  bookingIntent?: string;
  bookingRefund?: string;
  stripeConnectedAccount?: string;
  stripeConnectedInvoice?: string;
  stripeInvoice?: string;
  stripeConnectedPaymentIntent?: string;
  stripeCustomer?: string;
  stripe_connected_customer?: string;
  stripe_connected_session?: string;
  stripe_platform_payment_intent?: string;
  stripe_platform_refund?: string;
  stripe_fee?: string;
  quote_digest?: string;
  stripe_charge?: string;
  stripe_charge_status?: string;
  stripe_transfer_id?: string;
  stripe_transfer_amount?: number;
  sponsorship_fee_transfer_id?: string;
  sponsorship_transfer_id?: string;
  sponsorship_partner_transfer_id?: string;
  sponsorship_cancellation_transfer_id?: string;
  courier_refund?: string;
  contributionIntent?: string;
  contributionRefund?: string;
}
